import { httpsCallable } from "firebase/functions";
import { functions } from "../FirebaseConfig";
import { resolveImageSrc } from "./imageUrl";

/** Wrappers around the realEstateFn callables. */
export const createRealEstateListing = async (payload) => {
  const fn = httpsCallable(functions, "createRealEstateListing");
  const res = await fn(payload || {});
  return res.data;
};

export const updateRealEstateListing = async (id, updates) => {
  const fn = httpsCallable(functions, "updateRealEstateListing");
  const res = await fn({ id, ...updates });
  return res.data;
};

// Status-only change (approve / take down). Gated on `listings.approve` so
// moderators can use it without full-edit rights.
export const setRealEstateListingStatus = async (id, status) => {
  const fn = httpsCallable(functions, "setRealEstateListingStatus");
  const res = await fn({ id, status });
  return res.data;
};

export const deleteRealEstateListing = async (id, { hard = false } = {}) => {
  const fn = httpsCallable(functions, "deleteRealEstateListing");
  const res = await fn({ id, hard });
  return res.data;
};

/**
 * Server-side fetch of an external image into the Guild's own Storage
 * bucket so the listing keeps working even if the original host goes
 * away. Returns { url, storagePath, contentType, bytes, sourceUrl, importedAt }.
 */
export const importRealEstateImage = async ({ listingId, sourceUrl }) => {
  const fn = httpsCallable(functions, "importRealEstateImage");
  const res = await fn({ listingId, sourceUrl });
  return res.data;
};

export const fetchRealEstateListings = async ({ includeInactive = false } = {}) => {
  const fn = httpsCallable(functions, "fetchRealEstateListings");
  const res = await fn({ includeInactive });
  return Array.isArray(res.data) ? res.data : [];
};

export const getRealEstateListing = async ({ id, pgId, slug } = {}) => {
  const fn = httpsCallable(functions, "getRealEstateListing");
  const res = await fn({ id, pgId, slug });
  return res.data;
};

export const listContactInquiries = async ({ source, propertyPgId, limit } = {}) => {
  const fn = httpsCallable(functions, "listContactInquiries");
  const res = await fn({ source, propertyPgId, limit });
  return Array.isArray(res.data) ? res.data : [];
};

export const updateContactInquiryStatus = async ({ id, status, adminNotes }) => {
  const fn = httpsCallable(functions, "updateContactInquiryStatus");
  const res = await fn({ id, status, adminNotes });
  return res.data;
};

// --- helpers ---

export const getPropertyHeroImage = (listing) => {
  if (!listing) return "";
  const idx = Number.isFinite(listing.heroImageIdx) ? listing.heroImageIdx : 0;
  if (Array.isArray(listing.images) && listing.images.length > 0) {
    return resolveImageSrc(listing.images[idx] || listing.images[0]);
  }
  return "";
};

export const formatPrice = (listing) => {
  if (listing?.priceLabel) return listing.priceLabel;
  if (typeof listing?.price !== "number" || listing.price <= 0) return "Price on request";
  const currency = listing.currency || "USD";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(listing.price);
  } catch {
    return `${currency} ${listing.price.toLocaleString()}`;
  }
};

export const formatSpecs = (listing) => {
  const parts = [];
  if (listing?.beds) parts.push(`${listing.beds} bed${listing.beds === 1 ? "" : "s"}`);
  if (listing?.baths) parts.push(`${listing.baths} bath${listing.baths === 1 ? "" : "s"}`);
  if (listing?.sqft) parts.push(`${listing.sqft.toLocaleString()} sqft`);
  if (listing?.lotAcres) parts.push(`${listing.lotAcres} ac`);
  return parts.join(" · ");
};

export const propertyHref = (listing) => {
  if (!listing) return "/properties";
  if (listing.slug) return `/properties/${listing.slug}`;
  if (listing.pgId) return `/properties/${listing.pgId.toLowerCase()}`;
  return `/properties/${listing.id}`;
};

export const PROPERTY_TYPE_OPTIONS = [
  { value: "villa",        label: "Villa" },
  { value: "condo",        label: "Condo" },
  { value: "house",        label: "House" },
  { value: "townhouse",    label: "Townhouse" },
  { value: "land",         label: "Land" },
  { value: "commercial",   label: "Commercial" },
  { value: "rental",       label: "Rental" },
  { value: "multi-family", label: "Multi-family" },
  { value: "other",        label: "Other" },
];

export const PROPERTY_STATUS_OPTIONS = [
  { value: "draft",      label: "Draft" },
  { value: "pending",    label: "Pending" },
  { value: "active",     label: "Active" },
  { value: "sold",       label: "Sold" },
  { value: "off-market", label: "Off market" },
];

export const ISLAND_OPTIONS = [
  { value: "st-thomas", label: "St. Thomas" },
  { value: "st-john",   label: "St. John" },
  { value: "st-croix",  label: "St. Croix" },
  { value: "water-island", label: "Water Island" },
];

export const WATER_SOURCE_OPTIONS = [
  { value: "",           label: "—" },
  { value: "cistern",    label: "Cistern" },
  { value: "well",       label: "Well" },
  { value: "municipal",  label: "Municipal (WAPA)" },
  { value: "mixed",      label: "Mixed (cistern + municipal)" },
  { value: "other",      label: "Other" },
];

export const SEWER_OPTIONS = [
  { value: "",              label: "—" },
  { value: "septic",        label: "Septic" },
  { value: "municipal",     label: "Municipal" },
  { value: "package-plant", label: "Package plant" },
  { value: "other",         label: "Other" },
];

export const AC_OPTIONS = [
  { value: "",        label: "—" },
  { value: "central", label: "Central A/C" },
  { value: "split",   label: "Mini-split" },
  { value: "window",  label: "Window units" },
  { value: "none",    label: "None" },
];

export const POOL_OPTIONS = [
  { value: "",             label: "—" },
  { value: "none",         label: "No pool" },
  { value: "in-ground",    label: "In-ground" },
  { value: "above-ground", label: "Above-ground" },
  { value: "plunge",       label: "Plunge" },
  { value: "community",    label: "Community pool" },
];
